import { useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Checkbox,
  cn,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  ScrollArea,
  V2Label,
  VisuallyHidden,
} from "web-design-system";
import { ArrowLeft, DownloadIcon, Loader2 } from "lucide-react";
import { useDownloadPdfReport } from "web-core/hooks";
import { handleError } from "@/utils/error";
import { ConditionalRenderer } from "@core/components/conditional-renderer";
import {
  SyncingLottieAnimation,
  SuccessLottieAnimation,
  WarningLottieAnimation,
} from "web-core/components";
import { isNonProduction } from "@/utils/common";

export type ReportPreviewSection = {
  id: string;
  label: string;
  children?: ReportPreviewSection[];
};

export interface ReportPreviewDialogProps extends ReportPreviewProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export interface ReportPreviewProps {
  reportUrl: string;
  sections: ReportPreviewSection[];
  title: string;
  downloadFileName?: string;
  download?: (url: string) => Promise<boolean>;
  back?: () => void;
  pdfDimensions?: { width: number; height: number };
  // Rendered in the footer, before the download actions.
  footerActions?: React.ReactNode;
}

function getLeafIds(section: ReportPreviewSection): string[] {
  if (!section.children || section.children.length === 0) return [section.id];
  return section.children.flatMap(getLeafIds);
}

function buildUrlWithSections(baseUrl: string, sectionIds: string[]) {
  try {
    const u = new URL(baseUrl);
    u.searchParams.delete("sections");
    sectionIds.forEach((id) => u.searchParams.append("sections", id));
    return u.toString();
  } catch {
    return baseUrl;
  }
}

function buildEmbedUrl(url: string) {
  try {
    const u = new URL(url);
    u.searchParams.delete("embed");
    u.searchParams.set("embed", "true");
    return u.toString();
  } catch {
    return url;
  }
}

/** Payload shape must stay aligned with `getToggleSectionId` in reports `Report`. */
function postToggleSectionMessage(
  iframe: HTMLIFrameElement | null,
  sectionId: string
) {
  iframe?.contentWindow?.postMessage(
    { type: "toggle" as const, sectionId },
    window.location.origin
  );
}

function postScrollToSectionMessage(
  iframe: HTMLIFrameElement | null,
  sectionId: string
) {
  iframe?.contentWindow?.postMessage(
    { type: "scroll" as const, sectionId },
    window.location.origin
  );
}

export function ReportPreviewDialog(props: ReportPreviewDialogProps) {
  const { open, onOpenChange, title } = props;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[960px] w-[95vw] h-[90vh] p-0 flex flex-col gap-0 overflow-hidden">
        <VisuallyHidden>
          <DialogHeader className="px-6 py-4 border-b shrink-0">
            <DialogTitle>{title}</DialogTitle>
          </DialogHeader>
        </VisuallyHidden>
        <ReportPreview {...props} />
      </DialogContent>
    </Dialog>
  );
}

export function ReportPreview(props: ReportPreviewProps) {
  const { reportUrl, sections, title, downloadFileName = "report.pdf" } = props;

  const reportSectionIds = useMemo(
    () => sections.flatMap(getLeafIds),
    [sections]
  );
  const [selectedIds, setSelectedIds] = useState<string[]>(reportSectionIds);

  useEffect(() => {
    setSelectedIds(reportSectionIds);
  }, [reportSectionIds]);

  const iframeRef = useRef<HTMLIFrameElement>(null);

  const stablePreviewUrl = useMemo(
    () => buildEmbedUrl(buildUrlWithSections(reportUrl, reportSectionIds)),
    [reportUrl, reportSectionIds]
  );

  const [downloadStatus, setDownloadStatus] = useState<
    "idle" | "downloading" | "success" | "error"
  >("idle");

  const [downloadPdf] = useDownloadPdfReport();
  const toggleSection = (id: string, val: boolean) => {
    setSelectedIds((prev) =>
      val ? [...prev, id] : prev.filter((s) => s !== id)
    );
    postToggleSectionMessage(iframeRef.current, id);
  };

  const isSectionSelected = (id: string) => selectedIds.includes(id);

  const handleDownload = async () => {
    try {
      setDownloadStatus("downloading");
      const url = buildUrlWithSections(reportUrl, selectedIds);
      let success = false;
      if (props.download) {
        success = await props.download(url);
      } else {
        success = await downloadPdf(url, downloadFileName, {
          loading: "Generating report",
          dimensions: props.pdfDimensions,
          hideToast: true,
        });
      }
      if (success) {
        setDownloadStatus("success");
      } else {
        setDownloadStatus("error");
      }
    } catch (e) {
      setDownloadStatus("error");
      handleError(e, "Failed to download PDF");
    } finally {
      setTimeout(() => setDownloadStatus("idle"), 3000);
    }
  };
  return (
    <div className="flex flex-col size-full">
      <div className="h-16 gap-3 px-6 text-xl border-b border-border items-center flex font-semibold">
        {props.back ? (
          <Button
            variant="ghost"
            className="-ml-2"
            size="icon-lg"
            onClick={props.back}
          >
            <ArrowLeft />
          </Button>
        ) : null}
        {title}
      </div>

      <div className="flex min-h-0 min-w-0 flex-1 overflow-hidden">
        <ScrollArea>
          <div className="flex w-75 shrink-0 flex-col border-r">
            <div className="p-6 flex sticky z-1 bg-background top-0 items-center justify-between">
              <span className="font-semibold text-sm">Report Sections</span>
            </div>
            <div className="flex flex-col gap-4 p-6 pt-0">
              {sections.map((section) => {
                if (section.children && section.children.length > 0) {
                  const childIds = section.children.map((c) => c.id);
                  const allSelected = childIds.every((id) =>
                    selectedIds.includes(id)
                  );
                  const someSelected = childIds.some((id) =>
                    selectedIds.includes(id)
                  );
                  const parentChecked = allSelected
                    ? true
                    : someSelected
                      ? "indeterminate"
                      : false;
                  return (
                    <div
                      key={section.id}
                      className="flex flex-col rounded-lg bg-card"
                    >
                      <div className="flex items-center gap-3 min-h-12 px-3 py-1.5 rounded-lg select-none">
                        <Checkbox
                          className="shrink-0"
                          checked={parentChecked}
                          id={section.id}
                          onCheckedChange={(v) => {
                            const val = !!v;
                            setSelectedIds((prev) =>
                              val
                                ? [...new Set([...prev, ...childIds])]
                                : prev.filter((id) => !childIds.includes(id))
                            );
                            childIds.forEach((id) =>
                              postToggleSectionMessage(iframeRef.current, id)
                            );
                          }}
                        />
                        <V2Label className="text-sm" htmlFor={section.id}>
                          {section.label}
                        </V2Label>
                      </div>
                      <div className="flex flex-col pl-4">
                        {section.children.map((child) => {
                          const isSelected = isSectionSelected(child.id);
                          return (
                            <div
                              key={child.id}
                              className="flex items-center group min-h-10 gap-3 px-3 py-1.5 rounded-lg select-none"
                            >
                              <Checkbox
                                className="shrink-0"
                                checked={isSelected}
                                id={child.id}
                                onCheckedChange={(v) =>
                                  toggleSection(child.id, !!v)
                                }
                              />
                              <V2Label className="text-sm" htmlFor={child.id}>
                                {child.label}
                              </V2Label>
                              <Button
                                className={cn(
                                  "ml-auto invisible px-0",
                                  isSelected && "group-hover:visible"
                                )}
                                onClick={() =>
                                  postScrollToSectionMessage(
                                    iframeRef.current,
                                    child.id
                                  )
                                }
                                variant="link"
                                size="sm"
                              >
                                View
                              </Button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                }

                const isSelected = isSectionSelected(section.id);
                return (
                  <div
                    key={section.id}
                    className={cn(
                      "flex items-center group min-h-12 gap-3 group p-3 rounded-lg hover:bg-card/70 bg-card transition-colors select-none"
                    )}
                  >
                    <Checkbox
                      className="shrink-0"
                      checked={isSelected}
                      id={section.id}
                      onCheckedChange={(v) => {
                        toggleSection(section.id, !!v);
                      }}
                    />
                    <V2Label className="text-sm" htmlFor={section.id}>
                      {section.label}
                    </V2Label>

                    <Button
                      className={cn(
                        "ml-auto invisible px-0",
                        isSelected && "group-hover:visible"
                      )}
                      onClick={() =>
                        postScrollToSectionMessage(
                          iframeRef.current,
                          section.id
                        )
                      }
                      variant="link"
                      size="sm"
                    >
                      View
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>
        </ScrollArea>

        <div className="relative flex flex-1 flex-col overflow-hidden bg-secondary">
          <div className="flex min-h-0 flex-1 flex-col overflow-auto">
            <div className="mx-auto flex min-h-full w-full max-w-full flex-1 flex-col">
              <div className="absolute inset-0 overflow-x-hidden overflow-y-hidden p-6">
                {/* scale(0.5) + 200% box replaces non-standard `zoom` for consistent cross-browser layout */}
                <div className="h-[200%] w-[200%] origin-top-left scale-50">
                  <iframe
                    ref={iframeRef}
                    key={stablePreviewUrl}
                    title="Report Preview"
                    className="size-full min-h-full border-0"
                    src={stablePreviewUrl}
                  />
                </div>
              </div>
            </div>
          </div>
          <ConditionalRenderer condition={downloadStatus !== "idle"}>
            <div className="flex absolute inset-0 z-10 flex-1 flex-col bg-background items-center justify-center">
              <ConditionalRenderer condition={downloadStatus === "downloading"}>
                <SyncingLottieAnimation width={180} height={180} />
                <span className="text-sm text-muted-foreground">
                  Generating report. This may take upto 10 seconds
                </span>
              </ConditionalRenderer>

              <ConditionalRenderer condition={downloadStatus === "success"}>
                <SuccessLottieAnimation width={150} height={150} />
                <span className="text-sm text-muted-foreground">
                  Report downloaded successfully
                </span>
              </ConditionalRenderer>

              <ConditionalRenderer condition={downloadStatus === "error"}>
                <WarningLottieAnimation width={150} height={150} />
                <span className="text-sm text-muted-foreground">
                  Failed to download report. Please try again later.
                </span>
              </ConditionalRenderer>
            </div>
          </ConditionalRenderer>
        </div>
      </div>

      <div className="border-t px-6 gap-4 py-4 flex justify-end shrink-0">
        {props.footerActions}
        {isNonProduction() && (
          <Button
            variant="outline"
            onClick={() => {
              const url = buildUrlWithSections(reportUrl, selectedIds);
              window.open(url, "_blank", "noopener,noreferrer");
            }}
          >
            Preview
          </Button>
        )}
        <Button
          onClick={handleDownload}
          disabled={downloadStatus !== "idle" || selectedIds.length === 0}
        >
          {downloadStatus === "downloading" ? (
            <Loader2 className="animate-spin" />
          ) : (
            <DownloadIcon />
          )}
          Download PDF
        </Button>
      </div>
    </div>
  );
}
